import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatButton } from '@angular/material/button';
import { Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AccountModeService } from 'app/core/user/account-mode.service';

interface QuickLink {
    id: string;
    labelKey: string;
    icon: string;
    link: string;
}

@Component({
    selector       : 'shortcuts',
    templateUrl    : './shortcuts.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'shortcuts'
})
export class ShortcutsComponent implements OnInit, OnDestroy
{
    @ViewChild('shortcutsOrigin') private _shortcutsOrigin: MatButton;
    @ViewChild('shortcutsPanel') private _shortcutsPanel: TemplateRef<any>;

    quickLinks: QuickLink[] = [];
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    // Trainer quick links
    private _trainerLinks: QuickLink[] = [
        { id: 'profile', labelKey: 'quickMenu.myProfile', icon: 'heroicons_outline:user', link: '/app/profile' },
        { id: 'requests', labelKey: 'quickMenu.requests', icon: 'heroicons_outline:inbox', link: '/app/profile/requests' },
        { id: 'earnings', labelKey: 'quickMenu.earnings', icon: 'heroicons_outline:currency-euro', link: '/app/profile/earnings' },
    ];

    // Gym quick links
    private _gymLinks: QuickLink[] = [
        { id: 'dashboard', labelKey: 'quickMenu.dashboard', icon: 'heroicons_outline:view-grid', link: '/app/gym' },
        { id: 'trainers', labelKey: 'quickMenu.findTrainers', icon: 'heroicons_outline:user-group', link: '/app/contacts' },
        { id: 'billing', labelKey: 'quickMenu.billing', icon: 'heroicons_outline:currency-euro', link: '/app/gym/billing' },
        { id: 'settings', labelKey: 'quickMenu.settings', icon: 'heroicons_outline:cog', link: '/app/gym/settings' },
    ];

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _accountModeService: AccountModeService,
        private _translateService: TranslateService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef
    ) {}

    ngOnInit(): void {
        // Subscribe to mode changes
        this._accountModeService.currentMode$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(mode => {
                this.quickLinks = mode === 'gym' ? this._gymLinks : this._trainerLinks;
                this._changeDetectorRef.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    openPanel(): void {
        if (!this._shortcutsPanel || !this._shortcutsOrigin) {
            return;
        }

        if (!this._overlayRef) {
            this._createOverlay();
        }

        this._overlayRef.attach(new TemplatePortal(this._shortcutsPanel, this._viewContainerRef));
    }

    closePanel(): void {
        this._overlayRef.detach();
    }

    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    private _createOverlay(): void {
        this._overlayRef = this._overlay.create({
            hasBackdrop     : true,
            backdropClass   : 'fuse-backdrop-on-mobile',
            scrollStrategy  : this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._shortcutsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
                    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
                    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
                    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' }
                ])
        });

        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }
}
